import { PORTRAIT_WIDTH, PORTRAIT_HEIGHT } from "./SlideGenerator.constants";

type TextSegment = { text: string; highlighted: boolean };

export const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
};

export const parseHighlightedText = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  const regex = /\*([^*]+)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), highlighted: false });
    }
    segments.push({ text: match[1], highlighted: true });
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlighted: false });
  }

  return segments;
};

export const wrapTextWithHighlights = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): TextSegment[][] => {
  const segments = parseHighlightedText(text);
  const lines: TextSegment[][] = [];
  let currentLine: TextSegment[] = [];
  let currentLineWidth = 0;

  for (const segment of segments) {
    const words = segment.text.split(" ");

    for (const word of words) {
      if (!word) continue;

      const wordWithSpace = currentLineWidth > 0 ? " " + word : word;
      const wordWidth = ctx.measureText(wordWithSpace).width;

      if (currentLineWidth + wordWidth > maxWidth && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = [];
        currentLineWidth = 0;
      }

      const prefix = currentLineWidth > 0 ? " " : "";
      currentLine.push({ text: prefix + word, highlighted: segment.highlighted });
      currentLineWidth += ctx.measureText(prefix + word).width;
    }
  }

  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  return lines;
};

export const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines;
};

export const drawWatermark = (
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  appName: string = "Tribed"
) => {
  const padding = 50;
  const fontSize = 36;
  const centerX = canvasWidth / 2;
  const y = canvasHeight - padding;

  ctx.save();

  ctx.font = `italic 600 ${fontSize}px "Georgia", "Times New Roman", serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const textWidth = ctx.measureText(appName).width;
  const boxPaddingH = 24;
  const boxPaddingV = 12;
  const boxWidth = textWidth + boxPaddingH * 2;
  const boxHeight = fontSize + boxPaddingV;

  ctx.globalAlpha = 0.6;
  ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
  ctx.beginPath();
  ctx.roundRect(centerX - boxWidth / 2, y - boxHeight / 2, boxWidth, boxHeight, boxHeight / 2);
  ctx.fill();

  ctx.globalAlpha = 1;
  ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(appName, centerX, y);

  ctx.restore();
};

export const renderSlideWithTextOverlay = async (
  imageUrl: string,
  title: string,
  subtitle: string | undefined,
  slideNumber: number,
  totalSlides: number,
  primaryColor: string,
  communityName?: string
): Promise<string> => {
  const canvas = document.createElement("canvas");
  const width = PORTRAIT_WIDTH;
  const height = PORTRAIT_HEIGHT;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  try {
    const img = await loadImage(imageUrl);
    const scale = Math.max(width / img.width, height / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
  } catch {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#667eea");
    gradient.addColorStop(1, "#764ba2");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  // ── Classic TikTok text — stroke outline, no background, lower position ──────
  const hasSubtitle = !!subtitle?.trim();
  const titleFontSize = hasSubtitle ? 72 : 78;
  const titleLineHeight = titleFontSize * 1.38;
  const subtitleFontSize = 34;
  const subtitleLineHeight = subtitleFontSize * 1.35;
  const subtitleGap = hasSubtitle ? 26 : 0;
  const padding = 80;
  const maxTextWidth = width - padding * 2;

  ctx.font = `900 ${titleFontSize}px Impact, "Arial Black", "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";

  const titleLines = wrapTextWithHighlights(ctx, title, maxTextWidth);

  ctx.font = `700 ${subtitleFontSize}px "Helvetica Neue", Arial, sans-serif`;
  const subtitleLines = hasSubtitle
    ? wrapText(ctx, subtitle!.trim(), maxTextWidth - 40).slice(0, 3)
    : [];

  const totalTextHeight =
    titleLines.length * titleLineHeight
    + subtitleLines.length * subtitleLineHeight
    + (subtitleLines.length ? subtitleGap : 0);
  const textAnchorY = subtitleLines.length ? height * 0.64 : height * 0.68;
  const startY = textAnchorY - totalTextHeight / 2 + titleLineHeight / 2;

  ctx.font = `900 ${titleFontSize}px Impact, "Arial Black", "Helvetica Neue", Arial, sans-serif`;

  titleLines.forEach((lineSegments, lineIndex) => {
    const y = startY + lineIndex * titleLineHeight;

    // Measure total line width for centering
    let totalLineWidth = 0;
    for (const segment of lineSegments) {
      totalLineWidth += ctx.measureText(segment.text).width;
    }

    let x = (width - totalLineWidth) / 2;

    // Draw stroke pass first (black outline), then fill pass on top
    for (const segment of lineSegments) {
      const segW = ctx.measureText(segment.text).width;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.95)";
      ctx.lineWidth = 16;
      ctx.strokeText(segment.text, x + segW / 2, y);
      x += segW;
    }

    x = (width - totalLineWidth) / 2;
    for (const segment of lineSegments) {
      const segW = ctx.measureText(segment.text).width;
      ctx.fillStyle = segment.highlighted ? (primaryColor || "#FFD700") : "#FFFFFF";
      ctx.fillText(segment.text, x + segW / 2, y);
      x += segW;
    }
  });

  if (subtitleLines.length) {
    ctx.font = `700 ${subtitleFontSize}px "Helvetica Neue", Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const subtitleStartY =
      startY
      + titleLines.length * titleLineHeight
      + subtitleGap
      + subtitleLineHeight / 2;

    subtitleLines.forEach((line, lineIndex) => {
      const y = subtitleStartY + lineIndex * subtitleLineHeight;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.92)";
      ctx.lineWidth = 10;
      ctx.strokeText(line, width / 2, y);
      ctx.fillStyle = "#F8FAFC";
      ctx.fillText(line, width / 2, y);
    });
  }

  const indicatorY = height - 80;
  const dotSize = 10;
  const dotSpacing = 24;
  const totalDotsWidth = (totalSlides - 1) * dotSpacing;
  const startX = (width - totalDotsWidth) / 2;

  for (let i = 0; i < totalSlides; i++) {
    const dotX = startX + i * dotSpacing;
    ctx.beginPath();
    ctx.arc(dotX, indicatorY, dotSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = i === slideNumber ? "#FFFFFF" : "rgba(255, 255, 255, 0.4)";
    ctx.fill();
  }

  if (slideNumber === 0 && communityName) {
    drawWatermark(ctx, width, height, communityName + " App");
  }

  return canvas.toDataURL("image/png", 1.0);
};
